/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { cloneDeep } from 'lodash';
import { useEffect, useRef } from 'react';
import { DsmGrid } from '@dsm-dcs/design-system-react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from '../../../../../../_metronic/i18n/customUseIntl';
import { processAndStageStylesV2 } from '../../../../../../_metronic/layout';
import { UserProfilePrefs } from '../../../../../modules/Helpers/UserProfilePrefs';
import { unitLong } from '../../../../utils/unit-utils';
import { DialogContainer } from '../../CommonDataParts/DialogContainer2';
import { FormType } from '../../common';
import { PoultryInterventionFormDialogProps } from '../CommonDataParts/InterventionTypes';
import { CSSClassesList } from '../../../../helpers/helperTypes';
import { PoultryHatchingStageData, PoultryIntervention, PoultryOutputHatching as PoultryOutputInterventionHatching } from '../../../../models/Intervention/PoultryIntervention';
import { PoultryOutputHatching } from '../../../../models/Baseline/PoultryBaseline';
import DsmButtonControlGroup from '../../../helpers/DsmButtonControlGroup';
import RowTextFieldWithMetrics from '../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2';

const PoultryOutputHatchingDialog = ({
  formType = FormType.Add,
  stageIndex = 0,
  formVisible,
  handleCancel,
  baseline,
}: PoultryInterventionFormDialogProps) => {
  const intl = useIntl();
  const classes = processAndStageStylesV2() as CSSClassesList;
  const userProfile = UserProfilePrefs.getInstance();
  const userUOM = userProfile.getUserUnitPrefs();
  const barnOutputMassPrecision = userProfile.getUnitBarnOutputMassPrecision() as number;
  const barnOutputMassUnit = unitLong(userUOM.unitBarnOutputMass as string);
  const barnSmallQuantityUnits = unitLong(userUOM.unitBarnSmallQuantityUnits as string);
  const currResetValue = useRef<PoultryOutputInterventionHatching>();
  const formTitle = intl.formatMessage({id: 'SUSTELL.PROCESS.DIALOG.STAGE.OUTPUT.TITLE',});
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  const fieldItemPrefix = `stages.${stageIndex}.stageData.output`;
  const fc = useFormContext();

  useEffect(() => {
    if (formVisible) {
      currResetValue.current = cloneDeep(fc.getValues(fieldItemPrefix)) || {};
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formVisible]);

  const handleResetClick = () => {
    if (currResetValue.current) {
      const resetObject = { ...fc.getValues() } as PoultryIntervention;
      const stageData = resetObject?.stages?.[stageIndex]?.stageData as PoultryHatchingStageData;
      if (stageData?.output) {
        stageData.output = {...currResetValue.current};
        fc.reset(resetObject, {errors: true});
      }
    }
    handleCancel();
  };

  const output = baseline?.stages?.[stageIndex]?.stageData?.output as PoultryOutputHatching;

  return (
    <DialogContainer
      formVisible={formVisible}
      handleClose={handleResetClick}
      iconCode="arrows/arrow-circle-broken-right"
      formTitle={formTitle}
      datasetType="intervention"
    >
      <DsmGrid className={classes.dsmGridOneColumnIntervention}>
        <RowTextFieldWithMetrics
          name={`${fieldItemPrefix}.totalToStage`}
          label={intl.formatMessage({id: 'SUSTELL.STAGE.POULTRY.OUTPUT.HATCHING.TOTAL_TO_STAGE',})}
          type="number"
          tooltip=' '
          metricUnit=''
          baseline={output?.totalToStage}
          disabled={formType === FormType.View}
        />
        <RowTextFieldWithMetrics
          name={`${fieldItemPrefix}.averageWeight`}
          label={intl.formatMessage({id: 'SUSTELL.STAGE.POULTRY.OUTPUT.HATCHING.AVERAGE_WEIGHT'})}
          type="number"
          tooltip=' '
          metricUnit={barnSmallQuantityUnits}
          precision={barnOutputMassPrecision}
          baseline={output?.averageWeight}
          disabled={formType === FormType.View}
        />
        <RowTextFieldWithMetrics
          name={`${fieldItemPrefix}.totalSold`}
          label={intl.formatMessage({id: "SUSTELL.STAGE.POULTRY.OUTPUT.HATCHING.TOTAL_SOLD"})}
          type="number"
          tooltip=' '
          metricUnit=''
          baseline={output?.totalSold}
          disabled={formType === FormType.View}
        />
        <RowTextFieldWithMetrics
          name={`${fieldItemPrefix}.price`}
          label={intl.formatMessage({id: 'SUSTELL.STAGE.POULTRY.OUTPUT.HATCHING.PRICE'})}
          type="number"
          tooltip=' '
          metricUnit={intl.formatMessage({id: "SUSTELL.STAGE.POULTRY.OUTPUT.HATCHING.PRICE.PLACEHOLDER"})}
          baseline={output?.price}
          disabled={formType === FormType.View}
        />
        <RowTextFieldWithMetrics
          name={`${fieldItemPrefix}.lossRate`}
          label={intl.formatMessage({id: 'SUSTELL.STAGE.POULTRY.OUTPUT.HATCHING.LOSS'})}
          type="number"
          tooltip=' '
          metricUnit={barnOutputMassUnit}
          baseline={output?.lossRate}
          disabled={formType === FormType.View}
        />
        <RowTextFieldWithMetrics
          name={`${fieldItemPrefix}.priceOfLossesSold`}
          label={intl.formatMessage({id: 'SUSTELL.STAGE.POULTRY.OUTPUT.HATCHING.PRICE_LOSS'})}
          type="number"
          tooltip=' '
          metricUnit={intl.formatMessage({ id: "SUSTELL.STAGE.POULTRY.OUTPUT.HATCHING.PRICE_LOSS.PLACEHOLDER"}, { unit: barnOutputMassUnit } )}
          baseline={output?.priceOfLossesSold}
          disabled={formType === FormType.View}
        />
        <RowTextFieldWithMetrics
          name={`${fieldItemPrefix}.totalEggShellsSold`}
          label={intl.formatMessage({id: 'SUSTELL.STAGE.POULTRY.OUTPUT.HATCHING.TOTAL_EGG_SHELLS_SOLD'})}
          type="number"
          tooltip=' '
          metricUnit={barnOutputMassUnit}
          baseline={output?.totalEggShellsSold}
          disabled={formType === FormType.View}
        />
      </DsmGrid>
      <DsmButtonControlGroup
        cancelHandler={handleResetClick}
        saveHandler={handleCancel}
        saveLabel={intl.formatMessage({ id: 'GENERAL.CONFIRM' })}
      />
    </DialogContainer>
  );
};

export default PoultryOutputHatchingDialog;
